import cn from 'classnames';
import debounce from 'lodash.debounce';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Dispatch } from 'redux';

import { ROUTE_KNM } from 'CorePackage/MainModule/routes/routes';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  knmPmGetPlanKnmSelectOptions,
  knmPmOpenPlanKnmMessageModal,
} from 'KnmPmPackage/MainModule/actions/knmPmActions';
import { getPlanKnmFilter } from 'KnmPmPackage/MainModule/common/getPlanKnmFilter';
import { isPlanKnm } from 'KnmPmPackage/MainModule/common/isPlanKnm';
import { KnmPmPlanKnmMessageTypesEnum } from 'KnmPmPackage/MainModule/enums/KnmPmPlanKnmMessageTypesEnum';
import { IKnmPmForm } from 'KnmPmPackage/MainModule/interfaces/IKnmPmForm';
import { IKnmPmFormReason } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormReason';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { Tooltip } from 'UIPackage/MainModule/components/Tooltip';
import { Hint } from 'CorePackage/MainModule/components/Hint';

import { isKnmMultiOrganizations } from '../common/isKnmMultiOrganizations';
import { IKnmPmPlanKnmSelectOption } from '../interfaces/IKnmPmPlanKnmSelectOption';
import styles from './PmReasons.module.scss';

interface IPmReasonsLinkProps {
  reasonFieldValue: IKnmPmFormReason;
  reasonsFieldName: string;
  isDisabledReasonPlanKnm?: boolean;
  formRef?: IKnmFormRef;
  sIndex: number;
}

export const PmReasonsLink = ({
  reasonFieldValue,
  reasonsFieldName,
  isDisabledReasonPlanKnm,
  sIndex,
  formRef,
}: IPmReasonsLinkProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [inputValue, setInputValue] = useState('');

  const handleInputChange = useCallback((value: string) => {
    setInputValue(value.replace(/\D/g, ''));
  }, []);

  const handleReasonPlanKnmChange = (index: number) => (value: IKnmPmPlanKnmSelectOption) => {
    formRef?.current.change(`reasonsPm[${index}].knmIdLink` as keyof IKnmPmForm, value?.erpId);
    const isMultiOrganization = isKnmMultiOrganizations(value);

    if (value.kind === 'Рейдовый осмотр' && isMultiOrganization) {
      dispatch(knmPmOpenPlanKnmMessageModal(KnmPmPlanKnmMessageTypesEnum.EXCLUDE_CONTROLLING_SUBJECT, value.erpId));
    } else {
      dispatch(knmPmOpenPlanKnmMessageModal(KnmPmPlanKnmMessageTypesEnum.EXCLUDE_KNM, value.erpId));
    }
  };

  const reasonKnmIdLinkLoad = useCallback(
    debounce((erpId: string, callback: (fn: unknown) => void) => {
      const { knoOrganizationPm, supervisionTypePm, startDate, inn, ogrn, organizationName, reasonsPm } =
        formRef?.current.getState().values ?? {};

      if (knoOrganizationPm == null || supervisionTypePm == null || !startDate || !reasonsPm?.some(isPlanKnm)) {
        callback([]);

        return;
      }

      const filter = getPlanKnmFilter(
        startDate,
        supervisionTypePm,
        knoOrganizationPm,
        inn || undefined,
        ogrn || undefined,
        organizationName || undefined,
        erpId,
      );

      dispatch(knmPmGetPlanKnmSelectOptions(filter, callback));
    }, 300),
    [],
  );

  return (
    <div className={cn('shared-group-label', styles.PlanKnmGroup)}>
      <div className={styles.PlanKnmTitle}>
        Ссылка на другой паспорт
        <Tooltip
          placement="right"
          type="info"
          content={
            <div className="shared-tooltip-info-content">
              Укажите номер планового КНМ в статусе «Ожидает проведения» в утверждённом плане. В КНМ должны быть такие
              же КНО, вид контроля, год проведения и контролируемое лицо
            </div>
          }
        >
          <span className="shared-tooltip-info-icon" />
        </Tooltip>
      </div>
      {reasonFieldValue.planKnm != null && (
        <Link className={styles.PlanKnmLink} to={ROUTE_KNM(reasonFieldValue.planKnm.id)} target="blank">
          {reasonFieldValue.planKnm.erpId}
        </Link>
      )}
      <SelectInputField
        placeholder="Введите номер паспорта, выберите из выпадающего списка"
        id={`${reasonsFieldName}.planKnm`}
        name={`${reasonsFieldName}.planKnm`}
        disabled={isDisabledReasonPlanKnm}
        size="medium"
        isAsync
        isSearchable
        loadingMessage="Поиск..."
        noOptionsMessage="КНМ, соответствующие указанному учетному номеру, не найдены"
        onChange={handleReasonPlanKnmChange(sIndex)}
        loadOptions={reasonKnmIdLinkLoad}
        cacheOptions={false}
        inputValue={inputValue}
        onInputChange={handleInputChange}
      />
      <Hint text={'Заполните контролируемое лицо'} />
    </div>
  );
};
